// Design System Card Component
// Unified card component with all variants and features

import React, { useState, forwardRef } from 'react';
import { 
  Card as MuiCard, 
  CardContent, 
  CardActions, 
  CardHeader, 
  CardMedia,
  styled,
  alpha
} from '@mui/material';
import PropTypes from 'prop-types';
import { colorTokens, spacingTokens, borderTokens, shadowTokens, transitionTokens } from '../../tokens';

// Styled Card with design system tokens
const StyledCard = styled(MuiCard, {
  shouldForwardProp: (prop) => !['magical', 'level', 'interactive', 'selected'].includes(prop),
})(({ theme, magical, level = 1, interactive, selected }) => {
  const mode = theme.palette.mode;
  const colors = mode === 'dark' ? colorTokens.dark : colorTokens.light;
  
  const baseStyles = {
    position: 'relative',
    borderRadius: borderTokens.lg,
    backgroundColor: colors.background.paper,
    transition: transitionTokens.standard,
    overflow: 'hidden',
    border: `1px solid ${alpha(colors.divider, 0.1)}`,
    
    // Ensure content stays above effects
    '& > *': {
      position: 'relative',
      zIndex: 1,
    },
  };
  
  // Interactive hover effects
  const interactiveStyles = interactive ? {
    cursor: 'pointer',
    
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: shadowTokens.hover,
      borderColor: alpha(colors.primary.main, 0.3),
    },
    
    '&:active': {
      transform: 'translateY(-2px)',
    },
  } : {};
  
  // Selected state
  const selectedStyles = selected ? {
    borderColor: colors.primary.main,
    borderWidth: 2,
    boxShadow: `0 0 0 3px ${alpha(colors.primary.main, 0.1)}`,
  } : {};
  
  // Magical effects based on level
  const magicalStyles = magical ? {
    // Level 1: Subtle elevation on hover
    ...(level === 1 && {
      '&:hover': {
        transform: 'translateY(-8px)',
        boxShadow: shadowTokens.elevated,
      },
    }),
    
    // Level 2: Gradient border
    ...(level === 2 && {
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        borderRadius: 'inherit',
        padding: '2px',
        background: `linear-gradient(135deg, ${colors.primary.main}, ${colors.secondary.main})`,
        backgroundSize: '200% 200%',
        mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
        maskComposite: 'exclude',
        animation: 'gradient-shift 3s ease infinite',
        pointerEvents: 'none',
        zIndex: 0,
      },
      
      '@keyframes gradient-shift': {
        '0%, 100%': { backgroundPosition: '0% 50%' },
        '50%': { backgroundPosition: '100% 50%' },
      },
      
      '&:hover': {
        transform: 'translateY(-10px) scale(1.02)',
        boxShadow: shadowTokens.elevatedHover,
      },
    }),
    
    // Level 3: Full glow effect
    ...(level === 3 && {
      animation: 'glow 3s ease-in-out infinite',
      
      '@keyframes glow': {
        '0%, 100%': {
          boxShadow: `0 0 20px ${alpha(colors.primary.main, 0.3)}`,
        },
        '50%': {
          boxShadow: `0 0 30px ${alpha(colors.primary.main, 0.5)}`,
        },
      },
      
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        borderRadius: 'inherit',
        padding: '2px',
        background: `linear-gradient(135deg, ${colors.primary.main}, ${colors.secondary.main}, ${colors.primary.light})`,
        backgroundSize: '300% 300%',
        mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
        maskComposite: 'exclude',
        animation: 'gradient-flow 5s ease infinite',
        pointerEvents: 'none',
        zIndex: 0,
      },
      
      '@keyframes gradient-flow': {
        '0%': { backgroundPosition: '0% 50%' },
        '50%': { backgroundPosition: '100% 50%' },
        '100%': { backgroundPosition: '0% 50%' },
      },
      
      '&:hover': {
        transform: 'translateY(-12px) scale(1.03)',
        boxShadow: `0 20px 40px ${alpha(colors.primary.main, 0.3)}`,
      },
    }),
  } : {};
  
  return {
    ...baseStyles,
    ...interactiveStyles,
    ...selectedStyles,
    ...magicalStyles,
  };
});

/**
 * Card - Unified card component for the design system
 * 
 * Features:
 * - Consistent styling with design system tokens
 * - Interactive hover states
 * - Selected/active states
 * - Magical effects with 3 levels
 * - Support for header, media, content, and actions
 * - Responsive design
 * 
 * @example
 * // Basic card
 * <Card>
 *   <CardContent>
 *     <Typography>Card content</Typography>
 *   </CardContent>
 * </Card>
 * 
 * // Interactive card with header
 * <Card interactive title="Project Name" subheader="Due in 3 days">
 *   <CardContent>...</CardContent>
 *   <CardActions>
 *     <Button>View</Button>
 *   </CardActions>
 * </Card>
 * 
 * // Magical card with image
 * <Card magical level={2} image="/project-image.jpg">
 *   <CardContent>...</CardContent>
 * </Card>
 */
const Card = forwardRef(({
  children,
  magical = false,
  level = 1,
  interactive = false,
  selected = false,
  raised = false,
  
  // Header props
  title,
  subheader,
  avatar,
  action,
  headerProps,
  
  // Media props
  image,
  imageHeight = 200,
  imageAlt,
  mediaProps,
  
  // Actions
  actions,
  actionsProps,
  
  // Content props
  contentProps,
  
  // Event handlers
  onClick,
  onMouseEnter,
  onMouseLeave,
  
  ...props
}, ref) => {
  const [isHovered, setIsHovered] = useState(false);
  
  // Handle hover events
  const handleMouseEnter = (e) => {
    setIsHovered(true);
    onMouseEnter?.(e);
  };
  
  const handleMouseLeave = (e) => {
    setIsHovered(false);
    onMouseLeave?.(e);
  };
  
  // Handle click for interactive cards
  const handleClick = (e) => {
    if (interactive || onClick) {
      onClick?.(e);
    }
  };
  
  return (
    <StyledCard
      ref={ref}
      magical={magical}
      level={level}
      interactive={interactive || !!onClick}
      selected={selected}
      raised={raised || isHovered}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...props}
    >
      {/* Header */}
      {title && (
        <CardHeader
          title={title}
          subheader={subheader}
          avatar={avatar}
          action={action}
          {...headerProps}
        />
      )}
      
      {/* Media */}
      {image && (
        <CardMedia
          component="img"
          height={imageHeight}
          image={image}
          alt={imageAlt || title || 'Card image'}
          {...mediaProps}
        />
      )}
      
      {/* Content */}
      {children && (
        <CardContent {...contentProps}>
          {children}
        </CardContent>
      )}
      
      {/* Actions */}
      {actions && (
        <CardActions {...actionsProps}>
          {actions}
        </CardActions>
      )}
    </StyledCard>
  );
});

Card.propTypes = {
  children: PropTypes.node,
  magical: PropTypes.bool,
  level: PropTypes.oneOf([1, 2, 3]),
  interactive: PropTypes.bool,
  selected: PropTypes.bool,
  raised: PropTypes.bool,
  
  // Header props
  title: PropTypes.node,
  subheader: PropTypes.node,
  avatar: PropTypes.node,
  action: PropTypes.node,
  headerProps: PropTypes.object,
  
  // Media props
  image: PropTypes.string,
  imageHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  imageAlt: PropTypes.string,
  mediaProps: PropTypes.object,
  
  // Actions
  actions: PropTypes.node,
  actionsProps: PropTypes.object,
  
  // Content props
  contentProps: PropTypes.object,
  
  // Event handlers
  onClick: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
};

Card.displayName = 'Card';

// Export subcomponents for convenience
export { CardContent, CardActions, CardHeader, CardMedia } from '@mui/material';
export default Card;